import { Alert, Text, View, Pressable, Platform, PanResponder, FlatList } from 'react-native';
import DefaultScreen from '@components/Global/DefaultScreenView';
import React, { useEffect, useMemo, useRef, useState, useCallback, useContext } from 'react';
import { HIDE_TOOLBAR, CHANGE_SELECTED_ITEM, CHANGE_DATA_TAGS, REPLACE_HANDLER_SCHEMA, CLEAR_UPLOADER_HISTORY, ADD_DATA_ITEM, CLEAR_HANDLER_DATA } from '@reduxLocal/actions/types';
import { DefaultRootState } from '@reduxLocal/persistState';
import { Ionicons, FontAwesome } from '@expo/vector-icons';
import { connect, useDispatch, useSelector } from 'react-redux'
import SchemaComponent from './components/SchemaComponent';
import BottomSheet, {
  BottomSheetModal,
  BottomSheetModalProvider,
} from '@gorhom/bottom-sheet';
import * as Animatable from 'react-native-animatable';
import styles from '@stylesheet';
import { Feather } from '@expo/vector-icons';
import DropDownView from '@components/Global/DropDownView';
import { dispatchToolbar } from './utils/dispatchToolbar';
import { NavigationHelpers, useFocusEffect, } from '@react-navigation/native';
import apis from '@api/api';
import { get } from 'lodash';
import getGlobalUser from '@hooks/getGlobalUser';
import AnimatedLottieView from '@utils/lottieAnimation';
import { ToolbarContext } from '@hooks/toolbarContext';
import { Title } from '@constants/Themed';
import DraggableFlatList from 'react-native-draggable-flatlist';
import LottieView from 'lottie-react-native';
import { Spinner } from 'tamagui';


type DragDetectorProps = {
  index: number;
  onDragEnter: (index: number) => void;
  draggedItem: any;
};

const DragDetector: React.FC<DragDetectorProps> = ({ draggedItem, index, onDragEnter }) => {
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const dispatch = useDispatch();
  const { setDraggedItem } = useContext(ToolbarContext);
  // Get current array length to validate index
  const currentDataLength = useSelector((state: DefaultRootState) => state.uploadPreview.present.dataHeld.length);

  const handleDragOver = (event: React.DragEvent) => {
    if (Platform.OS !== 'web') return;
    event.preventDefault(); // Required to allow drop
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'move';
    setIsDraggingOver(true);
    if (onDragEnter) {
      onDragEnter(index);
    }
  };

  const handleDragLeave = (event: React.DragEvent) => {
    if (Platform.OS !== 'web') return;
    // Only clear if we're actually leaving the drop zone
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const x = event.clientX;
    const y = event.clientY;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsDraggingOver(false);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    if (Platform.OS !== 'web') return;
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingOver(false);

    console.log('DragDetector: Drop event received at index', index);

    // Try to get data from dataTransfer first (HTML5 drag/drop)
    let itemData = null;
    try {
      const dataTransferData = event.dataTransfer?.getData('application/json');
      if (dataTransferData) {
        itemData = JSON.parse(dataTransferData);
        console.log('DragDetector: Got item from dataTransfer:', itemData);
      }
    } catch (error) {
      console.warn('DragDetector: Failed to parse dataTransfer data:', error);
    }

    // Fallback to Context if dataTransfer didn't have data
    const itemToUse = itemData || draggedItem;

    if (!itemToUse) {
      console.warn('DragDetector: No item data available (neither dataTransfer nor context)');
      return;
    }

    try {
      const newItem = {
        title: itemToUse.toolName || itemToUse.title,
        type: itemToUse.id || itemToUse.type,
        value: itemToUse.id || itemToUse.value || itemToUse.type
      };

      // Calculate the insertion index
      // index === -1 means "before first item" (top detector) → insert at 0
      // index >= 0 means "after item at this index" → insert at index + 1
      let insertIndex = index === -1 ? 0 : index + 1;

      // Clamp index to valid range (0 to currentDataLength)
      // The max valid insert index is currentDataLength (appending to end)
      insertIndex = Math.max(0, Math.min(insertIndex, currentDataLength));

      console.log('DragDetector: Adding item to schema', {
        dragDetectorIndex: index,
        calculatedInsertIndex: insertIndex,
        currentArrayLength: currentDataLength,
        item: newItem,
        beforeInsert: `Array length: ${currentDataLength}, inserting at position: ${insertIndex}`
      });

      // Use a unique identifier for the new item to prevent key conflicts
      const itemWithId = {
        ...newItem,
        value: newItem.value || `${newItem.type}-${Date.now()}-${Math.random()}`
      };

      dispatch({ type: ADD_DATA_ITEM, payload: itemWithId, index: insertIndex });

      // Clear the dragged item from context after successful drop
      if (setDraggedItem) {
        setDraggedItem(null);
      }
    } catch (error) {
      console.error('DragDetector: Error handling drop:', error);
    }
  };

  if (Platform.OS !== 'web') {
    return null; // Only render on web
  }

  return (
    <div
      style={{
        minWidth: "80%",
        alignSelf: "center",
        borderRadius: 15,
        justifyContent: "center",
        position: "absolute",
        bottom: 30,
        height: '40px',
        marginBlock: -20,
        maxHeight: '40px',
        textAlign: 'center',
        backgroundColor: isDraggingOver ? 'lightgrey' : 'transparent',
        border: isDraggingOver ? '2px dashed #4CAF50' : '2px dashed transparent',
        transition: 'all 0.2s ease'
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    />
  );
};

type DataHandlerTypes = {
  navigation: NavigationHelpers<Record<string, object | undefined>, any>,
}

const DataHandler = ({ navigation }: DataHandlerTypes) => {
  const [loaded, setLoaded] = useState(false);
  const params = navigation.getState().routes[navigation.getState().index].params;
  const dataTracker = useSelector((state: DefaultRootState) => state.uploadPreview.present.dataHeld);
  const dataTitle = useSelector((state: DefaultRootState) => state.uploadPreview.present.dataTitle);
  const isUpload = useSelector((state: DefaultRootState) => state.uploadPreview.present.isUpload);
  const storedFile = useSelector((state: DefaultRootState) => state.uploadPreview.present.storedFile);

  // Debug: Log when dataTracker changes
  useEffect(() => {
    console.log('DataHandler: dataTracker updated, length:', dataTracker.length, 'items:', dataTracker.map(item => item.title || item.type));
  }, [dataTracker]);
  const [canGoBack, setCanGoBack] = useState(false);
  const [isSelected, setIsSelected] = useState(false);
  const [tags, setTags] = React.useState([]);
  const { draggedItem, setDraggedItem } = useContext(ToolbarContext);
  const reduxTags = useSelector((state: DefaultRootState) => state.uploadPreview.present.dataTags);
  const [listenerAdded, setListenederAdded] = useState(false);
  const bottomSheetRef = useRef<BottomSheet>(null);
  const [description, setDescription] = useState("");
  const userId = getGlobalUser();
  const id = get(params, 'id', null);
  const dispatch = useDispatch();
  // variables
  const [dataTitles, setDataTitles] = useState(dataTitle)
  const [change, setChange] = useState(false)
  const [modalState, setModalState] = useState(0);
  const [width, setWidth] = useState(1);



  // Sync tags to Redux when local state changes (but skip if they match to avoid loops)
  useEffect(() => {
    if (JSON.stringify(tags) !== JSON.stringify(reduxTags)) {
      dispatch({ type: CHANGE_DATA_TAGS, payload: tags });
    }
  }, [tags, dispatch, reduxTags]);

  // Sync local tags state when Redux tags change (e.g., when schema loads with tags)
  // Only update if local tags is empty or different from Redux
  useEffect(() => {
    if (reduxTags && Array.isArray(reduxTags) && JSON.stringify(tags) !== JSON.stringify(reduxTags)) {
      if (reduxTags.length > 0 || tags.length === 0) {
        setTags(reduxTags);
      }
    }
  }, [reduxTags]); // eslint-disable-line react-hooks/exhaustive-deps

  const _handleSetWidth = (width: number) => {
    setWidth(width);
  }


  // useFocusEffect hook for navigation listener
  useFocusEffect(
    React.useCallback(() => {
      setLoaded(false);

      if (id && id !== null) {
        // Load existing schema from API (this will replace whatever is in Redux)
        const getSchema = async () => {
          try {
            const res = await apis.getDataSchema(userId, id);
            const schemaData = res?.data?.data;
            console.log('DataHandler: Loading schema data:', schemaData);
            if (schemaData) {
              const parsedDataSchema = schemaData.dataSchema.map(item => ({
                ...item,
                type: item.type ? item.type : 'text'
              }));
              // Sync tags from schema
              if (schemaData.dataTags && Array.isArray(schemaData.dataTags)) {
                setTags(schemaData.dataTags);
              }
              // Dispatch new data - REPLACE_HANDLER_SCHEMA will replace existing data
              dispatch({
                type: REPLACE_HANDLER_SCHEMA,
                payload: {
                  dataHeld: parsedDataSchema,
                  dataTitle: schemaData.dataTitle,
                  schemaId: params,
                  dataTags: schemaData.dataTags || [],
                  description: schemaData.description || "TEST",
                },
              });
              console.log('DataHandler: Schema loaded with', parsedDataSchema.length, 'items');
              setLoaded(true);
            } else {
              console.warn('DataHandler: No schema data returned from API');
              setLoaded(true);
            }
          } catch (error) {
            console.error('DataHandler: Failed to fetch schema:', error);
            setLoaded(true);
          }
        };
        getSchema();
      } else {
        // No ID means we're coming from CreateDataScreen with template data
        // Just use what's already in Redux - don't clear or replace
        console.log('DataHandler: No ID provided, using existing Redux state');
        setLoaded(true);
      }
      // NOTE: CLEAR_UPLOADER_HISTORY removed from here as it was clearing state on every focus,
      // which would delete items that were dropped. History is only cleared on unmount now.
      const beforeRemoveListener = navigation.addListener('beforeRemove', handleBeforeRemove);
      dispatchToolbar();
      // Clean up the listener when the component loses focus
      // Note: HIDE_TOOLBAR is NOT called here to prevent toolbar dismissal when alerts pop up
      // Toolbar is only hidden when actually navigating away (see handleBeforeRemove)
      return () => {
        dispatch({ type: CLEAR_UPLOADER_HISTORY });
        dispatch({ type: CLEAR_HANDLER_DATA }); // Clear handler data when navigating away
        beforeRemoveListener();
        setLoaded(false);
      }
    }, [navigation, dispatchToolbar, id])
  );

  // Handler for beforeRemove event
  const handleBeforeRemove = (e) => {
    if (!canGoBack) {
      e.preventDefault();
      console.warn('preventing page change');
      if (Platform.OS === 'web') {
        //Create alert for web
        if (window.confirm('You have unsaved changes. Are you sure to discard them and leave the screen?')) {
          // setDataArray([]);
          dispatch({ type: CLEAR_UPLOADER_HISTORY })
          dispatch({ type: CLEAR_HANDLER_DATA }); // Clear handler data immediately
          dispatch({ type: HIDE_TOOLBAR });
          navigation.dispatch(e.data.action);
        }
      } else {
        Alert.alert(
          'Discard changes?',
          'You have unsaved changes. Are you sure to discard them and leave the screen?',
        [
          { text: "Don't leave", style: 'cancel', onPress: () => { } },
          {
            text: 'Discard',
            style: 'destructive',
            onPress: () => {
              // setDataArray([]);
              dispatch({ type: CLEAR_UPLOADER_HISTORY })
              dispatch({ type: CLEAR_HANDLER_DATA }); // Clear handler data immediately
              dispatch({ type: HIDE_TOOLBAR });
              navigation.dispatch(e.data.action);
            },
          },
        ]
      );
      }
    } else {
      // If canGoBack is true, hide toolbar as user is navigating away
      dispatch({ type: CLEAR_HANDLER_DATA }); // Clear handler data immediately
      dispatch({ type: HIDE_TOOLBAR });
    }
  };



  return loaded ? (
    <DefaultScreen noMargin scrollDisabled screenTitle={'Create New Data Model'}>
      <View className={Platform.select({ web: 'scroll-sm' })} onLayout={e => { console.log(e.nativeEvent.layout.width); _handleSetWidth(e.nativeEvent.layout.width) }}>
        <View style={{ flex: 1, alignSelf: 'stretch', minWidth: '100%' }} >
          <View style={{ top: 0 }}>
            <DragDetector index={-1} draggedItem={draggedItem} onDragEnter={() => console.log('entered drag placeholder', -1)} />
          </View>
          <FlatList
            data={dataTracker}
            className="scroll-sm"
            style={{ maxHeight: "65vh" }}
            contentContainerStyle={{ alignSelf: 'stretch', minWidth: '100%' }}
            renderItem={({ item, index }) => {
              return (
                <Pressable style={{ position: 'relative', alignSelf: "stretch", minWidth: '100%', zIndex: index * -1 }} key={(item.value || 5) + index}>
                  {({ hovered }) => (
                    <Animatable.View transition={['backgroundColor', 'paddingTop']} direction='alternate' style={{ flex: 1, backgroundColor: 'transparent', paddingTop: undefined, borderRadius: 20 }}>
                  <SchemaComponent width={width - (width * 0.18)} modalState={modalState} key={(item.value || 5) + index} dataField={item} indexLength={dataTracker.length} currentIndex={index} interaction={true} toolbarRef={bottomSheetRef} />
                  {/* Make a drag detector that highlights when user hovers in*/}
                  <DragDetector index={index} draggedItem={draggedItem} onDragEnter={() => console.log('entered drag placeholder', index)} />
                    </Animatable.View>
                  )}
                </Pressable>
              )
            }
            }
            ListEmptyComponent={(
              <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
                {__DEV__ && (
                  <View>
                    <Text>My Redux state is</Text>
                    <Text>
                      {dataTracker.length > 0 ? JSON.stringify(dataTracker, null, 2) : 'No data items available'}
                    </Text>
                    <Text>I retrieved</Text>
                    <Text>
                    </Text>
                  </View>
                )}
                <LottieView source={require("@assets/animations/drag.json")} autoPlay loop style={{ flex: 1, maxHeight: 200 }} />
                <Text style={{ fontSize: 18, color: 'grey' }}>No data items added yet.</Text>
                <Text style={{ fontSize: 14, color: 'grey' }}>Drag and drop items from the toolbar to add them here ={'>'}.</Text>
              </View>
            )}
            extraData={dataTracker.length}
            keyExtractor={(item, index) => {
              // Create a stable, unique key for each item
              // Use value if available, otherwise use index + type combination
              const baseKey = item.value || `item-${index}`;
              return `${baseKey}-${index}-${item.type || 'unknown'}`;
            }}
            // Note: onDragEnd is removed - it was conflicting with DragDetector drops
            // onDragEnd is for DraggableFlatList reordering, not for adding new items from toolbar
          />
        </View>
      </View>
    </DefaultScreen >
  ) : (
    <View style={{ flex: 1, justifyContent: 'center', alignContent: 'center', alignItems: 'center' }}>
        <Spinner size="large" color="primary" />
      <Text>Loading...</Text>
    </View>
  )
}

export default DataHandler;